import React, { useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { CheckCircle, ChevronDown, ChevronUp, Calculator, Users, Award, Zap, Globe, Mail } from 'lucide-react';

const tiers = [
  { name: 'Referral Partner', commission: 0.10, requirements: 'No upfront cost; refer clients', benefits: ['10% referral commission', 'Priority lead sharing'] },
  { name: 'Certified Partner', commission: 0.20, requirements: 'Formal agreement; completed training', benefits: ['20% project commission', 'Co-branded marketing', 'Priority support'] },
  { name: 'Strategic Partner', commission: 0.30, requirements: 'Annual revenue commitment or joint projects', benefits: ['30% commission', 'Joint business planning', 'Exclusivity in verticals', 'Dedicated account manager'] },
];

const whyBest = [
  'Highest commissions in the industry',
  'No upfront cost for referral partners',
  'Co-branded marketing & sales enablement',
  'Dedicated partner support & training',
  'Transparent, timely payouts',
  'Access to premium IT solutions',
];

const programComponents = [
  { title: 'Revenue Sharing & Incentives', content: 'Transparent tier-based commission structure. Performance bonuses for milestones. Incentive trips/events for top performers.' },
  { title: 'Partner Enablement', content: 'Onboarding & training, technical support, sandbox access, and early access to new offerings. Co-branded collateral.' },
  { title: 'Joint Marketing & Sales', content: 'Collaborative campaigns, webinars, workshops, trade shows, and CRM-integrated lead sharing.' },
  { title: 'Quality & Compliance', content: 'Standardized delivery, partner dashboards, quarterly reviews, and compliance with GDPR/ISO27001.' },
];

const workflow = [
  'Partner Recruitment',
  'Training & Certification',
  'Lead Distribution',
  'Project Collaboration',
  'Revenue Sharing',
  'Continuous Improvement',
];

const kpis = [
  'Number of active partners per tier',
  'Lead conversion rates via partners',
  'Partner-driven revenue %',
  'Partner satisfaction & engagement',
  'Time-to-close on partner leads',
];

const serviceOptions = [
  'Web Development',
  'Mobile App Development',
  'Cloud Solutions',
  'Digital Marketing',
  'SEO & SEM',
  'IT Consulting',
  'Custom Software',
  'E-Commerce Solutions',
  'ERP/CRM',
];

const PartnerPage: React.FC = () => {
  const [calcValue, setCalcValue] = useState('');
  const [expanded, setExpanded] = useState<number | null>(null);
  const [form, setForm] = useState<{
    name: string;
    contact: string;
    location: string;
    whatsapp: string;
    email: string;
    occupation: string;
    qualification: string;
    agree: boolean;
    services: string[];
    [key: string]: string | boolean | string[];
  }>({
    name: '',
    contact: '',
    location: '',
    whatsapp: '',
    email: '',
    occupation: '',
    qualification: '',
    agree: false,
    services: [],
  });
  const [formSubmitted, setFormSubmitted] = useState(false);
  const [formError, setFormError] = useState('');
  const [formTouched, setFormTouched] = useState<{[k:string]:boolean}>({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const deal = parseFloat(calcValue) || 0;

  const handleFormChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value, type } = e.target;
    setFormTouched({ ...formTouched, [name]: true });
    if (type === 'checkbox' && name === 'agree') {
      setForm({ ...form, agree: (e.target as HTMLInputElement).checked });
    } else if (type === 'checkbox' && name === 'services') {
      const val = value;
      setForm((prev) => ({
        ...prev,
        services: prev.services.includes(val)
          ? prev.services.filter((s) => s !== val)
          : [...prev.services, val],
      }));
    } else {
      setForm({ ...form, [name]: value });
    }
  };

  const handleFormSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setFormError('');
    
    // Validate all fields
    const requiredFields = ['name', 'contact', 'location', 'whatsapp', 'email', 'occupation', 'qualification'];
    const missingFields = requiredFields.filter(field => !form[field]);
    
    if (missingFields.length > 0 || !form.agree || form.services.length === 0) {
      setFormError('Please fill all required fields and agree to the transparency policy.');
      // Mark all fields as touched to show validation errors
      const touched = requiredFields.reduce((acc, field) => ({ ...acc, [field]: true }), {});
      setFormTouched({ ...touched, services: true });
      return;
    }
    
    setIsSubmitting(true);
    
    try {
      const response = await fetch('/api/partner', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(form),
      });

      const data = await response.json();

      if (response.ok) {
        setFormSubmitted(true);
        setFormError('');
        // Reset form
        setForm({
          name: '',
          contact: '',
          location: '',
          whatsapp: '',
          email: '',
          occupation: '',
          qualification: '',
          agree: false,
          services: [],
        });
        setFormTouched({});
      } else {
        throw new Error(data.error || 'Failed to submit application');
      }
    } catch (error) {
      console.error('Error submitting application:', error);
      setFormError('There was an error submitting your application. Please try again or contact us directly.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const validate = (field: string) => {
    if (!formTouched[field]) return '';
    if (!form[field] || (Array.isArray(form[field]) && form[field].length === 0)) return 'Required';
    if (field === 'email' && form.email && !/^\S+@\S+\.\S+$/.test(form.email)) return 'Enter a valid email';
    if (field === 'contact' && form.contact && !/^\+?\d{7,15}$/.test(form.contact)) return 'Enter a valid phone number';
    if (field === 'whatsapp' && form.whatsapp && !/^\+?\d{7,15}$/.test(form.whatsapp)) return 'Enter a valid WhatsApp number';
    return '';
  };

  return (
    <>
      <Helmet>
        <title>Partner Program | VB Entreprise</title>
      </Helmet>
      <div className="pt-8 bg-white min-h-screen text-gray-900">
        {/* Hero/Overview */}
        <section className="py-16 bg-gradient-to-br from-jordy-blue-50 to-jordy-blue-100 text-center">
          <h1 className="text-4xl md:text-6xl font-bold mb-4 text-jordy-blue-800">VB Entreprise Partnership Program</h1>
          <p className="text-xl md:text-2xl max-w-2xl mx-auto mb-6 text-gray-700">A strategic alliance model empowering businesses, freelancers, and consultants to co-sell and co-deliver premium IT solutions. Grow your revenue, credibility, and technical expertise with us.</p>
          <a href="#apply" className="inline-block mt-4 bg-jordy-blue-600 text-white px-8 py-4 rounded-lg font-semibold hover:bg-jordy-blue-700 transition-colors shadow-lg">Apply Now</a>
        </section>

        {/* Target Partner Profiles */}
        <section className="py-12 max-w-5xl mx-auto px-4">
          <h2 className="text-3xl font-bold mb-6 text-center">Who Should Partner?</h2>
          <div className="grid md:grid-cols-2 gap-8">
            <div className="bg-white rounded-xl shadow p-6 flex flex-col items-center">
              <Users className="w-10 h-10 text-jordy-blue-600 mb-2" />
              <h3 className="font-semibold text-lg mb-1">Technology Consultants & Freelancers</h3>
              <p className="text-gray-600 text-center">Independent experts seeking reliable IT service delivery support.</p>
            </div>
            <div className="bg-white rounded-xl shadow p-6 flex flex-col items-center">
              <Award className="w-10 h-10 text-jordy-blue-600 mb-2" />
              <h3 className="font-semibold text-lg mb-1">Marketing & Digital Agencies</h3>
              <p className="text-gray-600 text-center">Agencies wanting to offer IT development without building in-house tech teams.</p>
            </div>
            <div className="bg-white rounded-xl shadow p-6 flex flex-col items-center">
              <Zap className="w-10 h-10 text-jordy-blue-600 mb-2" />
              <h3 className="font-semibold text-lg mb-1">System Integrators & VARs</h3>
              <p className="text-gray-600 text-center">Companies looking to add custom software & cloud solutions.</p>
            </div>
            <div className="bg-white rounded-xl shadow p-6 flex flex-col items-center">
              <Globe className="w-10 h-10 text-jordy-blue-600 mb-2" />
              <h3 className="font-semibold text-lg mb-1">Industry-Specific Consultants</h3>
              <p className="text-gray-600 text-center">Niche players in finance, healthcare, retail, who need IT partners.</p>
            </div>
          </div>
        </section>

        {/* Partnership Tiers & Benefits */}
        <section className="py-12 bg-jordy-blue-50">
          <h2 className="text-3xl font-bold mb-8 text-center">Partnership Tiers & Benefits</h2>
          <div className="overflow-x-auto">
            <table className="min-w-full bg-white rounded-xl shadow text-left">
              <thead>
                <tr className="bg-jordy-blue-100">
                  <th className="py-3 px-4 font-semibold">Tier</th>
                  <th className="py-3 px-4 font-semibold">Requirements</th>
                  <th className="py-3 px-4 font-semibold">Benefits</th>
                  <th className="py-3 px-4 font-semibold">Commission</th>
                </tr>
              </thead>
              <tbody>
                {tiers.map((tier, idx) => (
                  <tr key={tier.name} className="border-b last:border-0">
                    <td className="py-3 px-4 font-semibold text-jordy-blue-700">{tier.name}</td>
                    <td className="py-3 px-4 text-gray-700">{tier.requirements}</td>
                    <td className="py-3 px-4">
                      <ul className="list-disc ml-5 text-gray-700">
                        {tier.benefits.map(b => <li key={b}>{b}</li>)}
                      </ul>
                    </td>
                    <td className="py-3 px-4 text-jordy-blue-800 font-bold">{tier.commission * 100}%</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>

        {/* Revenue Calculator */}
        <section className="py-12 max-w-3xl mx-auto px-4">
          <h2 className="text-2xl font-bold mb-4 flex items-center justify-center"><Calculator className="w-6 h-6 mr-2 text-jordy-blue-600" />Revenue Calculator</h2>
          <p className="text-gray-600 mb-4 text-center">Estimate your commission for a project/deal:</p>
          <div className="flex flex-col md:flex-row items-center justify-center gap-4 mb-4">
            <input
              type="number"
              min="0"
              value={calcValue}
              onChange={e => setCalcValue(e.target.value)}
              placeholder="Enter deal value (USD)"
              className="px-4 py-3 border border-jordy-blue-200 rounded-lg focus:ring-2 focus:ring-jordy-blue-500 w-60 text-lg"
            />
            <div className="flex flex-col md:flex-row gap-2 md:gap-4">
              {tiers.map(tier => (
                <div key={tier.name} className="bg-jordy-blue-100 rounded-lg px-4 py-2 text-center">
                  <div className="font-semibold text-jordy-blue-700 text-sm">{tier.name}</div>
                  <div className="text-lg font-bold text-jordy-blue-800">${deal ? (deal * tier.commission).toLocaleString() : '0'}</div>
                  <div className="text-xs text-gray-500">at {tier.commission * 100}%</div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Why This is the Best Program */}
        <section className="py-12 max-w-4xl mx-auto px-4">
          <h2 className="text-2xl font-bold mb-6 text-center">Why Join Our Partnership Program?</h2>
          <ul className="grid md:grid-cols-2 gap-4">
            {whyBest.map((reason, i) => (
              <li key={i} className="flex items-center bg-jordy-blue-50 rounded-lg p-4 text-jordy-blue-900 font-medium"><CheckCircle className="w-5 h-5 text-green-500 mr-2" />{reason}</li>
            ))}
          </ul>
        </section>

        {/* Core Program Components (Accordion) */}
        <section className="py-12 max-w-3xl mx-auto px-4">
          <h2 className="text-2xl font-bold mb-6 text-center">Core Program Components</h2>
          <div className="space-y-4">
            {programComponents.map((comp, i) => (
              <div key={comp.title} className="border border-jordy-blue-200 rounded-lg">
                <button type="button" className="w-full flex items-center justify-between px-4 py-3 font-semibold text-jordy-blue-700 focus:outline-none" onClick={() => setExpanded(expanded === i ? null : i)}>
                  {comp.title}
                  {expanded === i ? <ChevronUp className="w-5 h-5" /> : <ChevronDown className="w-5 h-5" />}
                </button>
                {expanded === i && <div className="px-4 pb-4 text-gray-700 animate-fadeInDown">{comp.content}</div>}
              </div>
            ))}
          </div>
        </section>

        {/* Engagement Workflow (Stepper) */}
        <section className="py-12 max-w-4xl mx-auto px-4">
          <h2 className="text-2xl font-bold mb-6 text-center">Engagement Workflow</h2>
          <div className="relative">
            <ol className="flex flex-col md:flex-row md:justify-between gap-6 md:gap-0 relative z-10">
              {workflow.map((step, i) => (
                <li key={step} className="flex-1 flex flex-col items-center relative">
                  <div className={`w-12 h-12 rounded-full flex items-center justify-center font-bold text-white mb-2 text-lg shadow-lg ${i === 0 ? 'bg-jordy-blue-600' : 'bg-jordy-blue-400'}`}>{i + 1}</div>
                  <span className="text-center text-gray-800 font-medium">{step}</span>
                </li>
              ))}
            </ol>
            {/* Connecting lines */}
            <div className="hidden md:block absolute top-5 left-0 right-0 z-0 h-0.5">
              <div className="w-full h-full flex">
                {workflow.slice(0, -1).map((_, i) => (
                  <div key={i} className="flex-1 flex items-center">
                    <div className="h-1 w-full bg-jordy-blue-300" style={{ marginLeft: i === 0 ? '50%' : 0, marginRight: i === workflow.length - 2 ? '50%' : 0 }}></div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* Technology & Automation Integration */}
        <section className="py-12 max-w-4xl mx-auto px-4">
          <h2 className="text-2xl font-bold mb-6 text-center">Technology & Automation</h2>
          <ul className="list-disc ml-6 text-gray-700 space-y-2">
            <li>Partner Portal for lead registration, resource access, and training modules</li>
            <li>Automated commission calculation and payout requests</li>
            <li>CRM integration (Zoho One, Perfex CRM, etc.)</li>
            <li>Certification tracking and enablement resources</li>
          </ul>
        </section>

        {/* KPIs & Communication */}
        <section className="py-12 max-w-4xl mx-auto px-4">
          <h2 className="text-2xl font-bold mb-6 text-center">KPIs & Communication</h2>
          <div className="grid md:grid-cols-2 gap-6">
            <div>
              <h3 className="font-semibold text-lg mb-2">Key Performance Indicators</h3>
              <ul className="list-disc ml-6 text-gray-700 space-y-1">
                {kpis.map(kpi => <li key={kpi}>{kpi}</li>)}
              </ul>
            </div>
            <div>
              <h3 className="font-semibold text-lg mb-2">Communication Strategy</h3>
              <ul className="list-disc ml-6 text-gray-700 space-y-1">
                <li>Monthly newsletters with updates, success stories, and incentives</li>
                <li>Dedicated partner webinars and Q&A sessions</li>
                <li>Annual partner summit (virtual or in-person)</li>
              </ul>
            </div>
          </div>
        </section>

        {/* Call to Action */}
        <section id="apply" className="py-16 bg-gradient-to-r from-jordy-blue-600 to-jordy-blue-800 text-white text-center">
          <h2 className="text-3xl font-bold mb-4">Ready to Grow Together?</h2>
          <p className="text-lg mb-8">Apply to become a VB Entreprise Partner and unlock new revenue streams, technical enablement, and business growth.</p>
          <div className="max-w-xl mx-auto bg-white text-gray-900 rounded-2xl shadow-2xl p-8 mt-8 border border-jordy-blue-100">
            {!formSubmitted ? (
              <form onSubmit={handleFormSubmit} className="space-y-6 text-left">
                {formError && <div className="text-red-500 text-sm text-center mb-2">{formError}</div>}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  {[
                    { label: 'Name', name: 'name', type: 'text' },
                    { label: 'Contact Number', name: 'contact', type: 'text' },
                    { label: 'Location', name: 'location', type: 'text' },
                    { label: 'Whatsapp Number', name: 'whatsapp', type: 'text' },
                    { label: 'Email', name: 'email', type: 'email' },
                    { label: 'Current Occupation', name: 'occupation', type: 'text' },
                    { label: 'Qualification', name: 'qualification', type: 'text' },
                  ].map((field) => (
                    <div key={field.name} className="relative">
                      <input
                        name={field.name}
                        type={field.type}
                        value={String(form[field.name] ?? '')}
                        onChange={handleFormChange}
                        onBlur={handleFormChange}
                        required
                        className={`peer w-full px-4 pt-6 pb-2 border ${validate(field.name) ? 'border-red-400' : 'border-jordy-blue-200'} rounded-lg focus:ring-2 focus:ring-jordy-blue-500 bg-transparent placeholder-transparent transition-all`}
                        placeholder={field.label}
                        autoComplete="off"
                        aria-required="true"
                        aria-invalid={!!validate(field.name)}
                      />
                      <label
                        htmlFor={field.name}
                        className={`absolute left-4 top-2 text-sm font-medium pointer-events-none transition-all duration-200
                          peer-placeholder-shown:top-5 peer-placeholder-shown:text-base peer-placeholder-shown:text-gray-400
                          peer-focus:top-2 peer-focus:text-sm peer-focus:text-jordy-blue-700
                          ${validate(field.name) ? 'text-red-500' : 'text-gray-700'}
                        `}
                      >
                        {field.label} <span className="text-red-500">*</span>
                      </label>
                      {validate(field.name) && <span className="text-xs text-red-500 absolute left-4 bottom-0 pb-1">{validate(field.name)}</span>}
                    </div>
                  ))}
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1 text-gray-700">Services you want to promote <span className="text-red-500">*</span></label>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                    {serviceOptions.map((service) => (
                      <label key={service} className="flex items-center space-x-2 rounded-lg px-2 py-1 hover:bg-jordy-blue-50 transition-colors">
                        <input
                          type="checkbox"
                          name="services"
                          value={service}
                          checked={Array.isArray(form.services) && form.services.includes(service)}
                          onChange={handleFormChange}
                          className="rounded border-jordy-blue-400 text-jordy-blue-600 focus:ring-jordy-blue-500"
                          required={formTouched['services'] && form.services.length === 0}
                        />
                        <span>{service}</span>
                      </label>
                    ))}
                  </div>
                  {formTouched['services'] && form.services.length === 0 && <span className="text-xs text-red-500">Required</span>}
                </div>
                <div className="flex items-center">
                  <input type="checkbox" name="agree" checked={form.agree} onChange={handleFormChange} className="mr-2 rounded border-jordy-blue-400 text-jordy-blue-600 focus:ring-jordy-blue-500" required />
                  <span className="text-sm">I agree to keep the process transparent <span className="text-red-500">*</span></span>
                </div>
                <button 
                  type="submit" 
                  disabled={isSubmitting}
                  className="w-full bg-jordy-blue-600 text-white py-3 rounded-lg font-semibold hover:bg-jordy-blue-700 transition-colors shadow-lg disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center"
                >
                  {isSubmitting ? (
                    <>
                      <span className="animate-spin mr-2">⏳</span>
                      Submitting...
                    </>
                  ) : (
                    'Submit Application'
                  )}
                </button>
              </form>
            ) : (
              <div className="text-center py-12">
                <CheckCircle className="w-16 h-16 text-green-500 mx-auto mb-4" />
                <h3 className="text-2xl font-bold text-gray-900 mb-2">Thank You!</h3>
                <p className="text-gray-600">Your partnership application has been received. We will contact you soon.</p>
              </div>
            )}
          </div>
        </section>
      </div>
    </>
  );
};

export default PartnerPage; 